import { graphql, Link } from "gatsby";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import React, { useEffect, useState, useRef } from "react";
import Grid, { Col } from "../components/grid/Grid";
import ItemTile from "../components/itemTile/ItemTile";
import AppLink from "../components/appLink/AppLink";
import Animate from "../components/animate/Animate";
import Meta from "../components/meta/Meta";
import Carousel from "../components/carousel/Carousel";
import LogoBoard from "../components/logoBoard/LogoBoard";
import Section from "../components/section/Section";
import ContactBanner from "../components/contactBanner/ContactBanner";
import Video from "../components/video/Video";
import Quote from "../components/quote/Quote";
import PackagesBlock from "../components/packagesBlock/PackagesBlock";
import { ShaderGradientCanvas, ShaderGradient } from '@shadergradient/react';

import IconEmployeeOwned from "../assets/IconEmployeeOwned";
import * as styles from "./index.module.css";
import { useScrollPosition, useWindowSize } from "../utils/hooks";

const Home = ({ data }) => {
  const windowHeight = useWindowSize().height;
  const windowWidth = useWindowSize().width;
  const scrollY = useScrollPosition();
  const scrollPages = Math.round((scrollY / windowHeight) * 100) / 100;
  const [play, togglePlay] = useState(false);
  useEffect(() => {
    togglePlay(true);
  });

  const isMobile = windowWidth <= 768;

  return (
    <article className={styles.homeWrapper}>
      <Section
        backgroundColor="black"
        backgroundVideo={
          <Video
            src={isMobile ? "https://assets.ustwo.com/video/home-hero-04-mobile.mp4" : "https://assets.ustwo.com/video/home-hero-05.mp4"}
            posterImage={data.content.hero.childImageSharp.gatsbyImageData}
            autoPlay
            stop={scrollPages > 0.5}
            type="mp4"
            description="Showcase reel of case studies"
            captions="Showcase reel of various case studies including The Body Coach (Joe Wicks on a workout), Peloton (A man using a Peloton bike with the Lanebreak App), Helios Fertility (App screens)."
          />
        }
        mobileBackgroundVideo={isMobile}
        lightOnDark
        className={styles.aboveFoldContent}
        style={{ overflow: "hidden", zIndex: "1" }}
        childrenBefore={<div className={styles.videoCover} style={{ opacity: 0.4 + (scrollPages * 1.5) }}></div>}
        verticalEnd
      >
        <div className={styles.aboveFoldText} style={{ opacity: 1 - (scrollPages * 4) }}>
          <Animate
            play={play}
            delay="0.2s"
            duration="0.6s"
            translateY="30px"
            fadeIn
            timing="cubic-bezier(0.18, 0.47, 0.27, 0.92)"
          >
            <div className={`h2 colorWhite noMargin`}>Get ready</div>
            <h1 className={`h1 h1SuperLarge colorWhite noMargin ${styles.strapline}`}>What's next is coming fast</h1>
          </Animate>
        </div>
      </Section>

      <div className={styles.homeContent}>
        <Section padding="both" backgroundColor="offBlack" className={styles.descriptionIntro}>

            <Animate
              play={play}
              delay="0.4s"
              duration="0.6s"
              translateY="30px"
              fadeIn
              timing="cubic-bezier(0.18, 0.47, 0.27, 0.92)"
            >
                <p className="h1 colorWhite noMargin">
                  For 20 years we've been imagining and launching <span>breakthrough digital experiences</span> so that businesses can grow with confidence in their future.
                </p>
            </Animate>

        </Section>

        <Section
          className={styles.aiBanner}
          backgroundVideo={
            <Video
              src="https://assets.ustwo.com/video/ai-banner-bg-video.mp4"
              autoPlay
              stop={scrollPages > 0.5}
              type="mp4"
              description="Background abstract moving colour gradients"
              noPauseButton
              ariaHidden="true"
            />
          }
        >
          <Grid className={styles.aiBannerTop}>
            <Col start={1} span={7}>
              <h2 className="h1 h1LessSuperLarge">{data.ai.frontmatter.tagline}</h2>
            </Col>
          </Grid>
          <Grid className={styles.aiBannerBottom}>
            <Col start={7} span={6}>
              <p className="largestBodyText">{data.ai.frontmatter.description}</p>
              <div>
                <Link 
                  to="/ai/" 
                  data-cabin-event="Home-to-AI" 
                  onClick={() => window.rybbit?.event("Home to AI")}
                  className={`button buttonFill textMarginTop ${styles.aiBannerButton}`}
                >
                  Learn more
                </Link>
              </div>
            </Col>
          </Grid>
        </Section>
    
        <PackagesBlock showLink />
      
        <Section padding="both" style={{ zIndex: 9 }} backgroundColor="white">
          <Grid verticalCenter className="textMarginBottomLarge">
            <h2 className="noMargin">
              Our work
            </h2>
          </Grid>
          <Grid columns={3}>
            {[data.loreal, data.inflection, data.klir].map((item, i) => (
              <ItemTile
                image={item.tallThumbnail ? item.tallThumbnail.childImageSharp : item.thumbnail?.childImageSharp}
                path={item.frontmatter.path}
                title={item.frontmatter.title}
                description={item.frontmatter.tagline}
                className={styles.caseStudy}
                key={`homeCaseStudy0${i + 1}`}
                height="tall"
              />
            ))}
          </Grid>
          <div className={styles.onwardLink}>
            <Link to="/work/" className="button buttonFill textMarginTop">All case studies</Link>
          </div>
        </Section>
        
        <Section backgroundColor="offWhite" padding="both">
          <Grid>
            <Col start={1} span={12}>
              <LogoBoard logos={data.logos.images} />
            </Col>
          </Grid>
        </Section>

        <Section 
          padding="both" 
          backgroundColor="mint"
        >
          <Grid padding="both">
            <Col start={1} span={11}>
              <Carousel page="Home" items={[
                <Quote
                  content={data.quote01.frontmatter.quote}
                  author={data.quote01.frontmatter.author}
                  authorTitle={data.quote01.frontmatter.authorTitle}
                  largestText
                />,
                <Quote
                  content={data.quote03.frontmatter.quote}
                  author={data.quote03.frontmatter.author}
                  authorTitle={data.quote03.frontmatter.authorTitle}
                  largestText
                />,
                <Quote
                  content={data.quote02.frontmatter.quote}
                  author={data.quote02.frontmatter.author}
                  authorTitle={data.quote02.frontmatter.authorTitle}
                  largestText
                />
              ]} />
            </Col>
          </Grid>
        </Section>

        <Section
          verticalCenter
          noInner
          className={styles.homeImage}
          backgroundColor="black"
          childrenAfter={
            <>
              <StaticImage className={styles.homeImageBg} src="https://assets.ustwo.com/images/people/barn-2023-group-04.jpg" alt="A group photo of employees at our annual social day, smiling, waving and cheering" />
              <div className={styles.studioNames}>
                <div className={`${styles.studioCity} ${styles.studioLisbon}`}>Lisbon</div>
                <div className={`${styles.studioCity} ${styles.studioLondon}`}>London</div>
                <div className={`${styles.studioCity} ${styles.studioMalmo}`}>Malmö</div>
                <div className={`${styles.studioCity} ${styles.studioNY}`}>New York</div>
                <div className={`${styles.studioCity} ${styles.studioTokyo}`}>Tokyo</div>
              </div>
            </>
          }
        />

        {/* B Corp */}
        <Section padding="both" backgroundColor="white">
          <Grid>
            <Col start={2} span={4}>
              <div className={styles.logos}>
                <Link to="/employee-owned" className={styles.eotLogo} title="Employee owned">
                  <IconEmployeeOwned />
                </Link>
                {data.bCorpLogo.childrenImageSharp[0] && (
                  <Link to="/b-corp" title="B Corp">
                    <GatsbyImage
                      image={data.bCorpLogo.childrenImageSharp[0].gatsbyImageData}
                      alt="B Corp logo"
                      className={styles.bCorpLogo}
                    />
                  </Link>
                )}
              </div>
            </Col>
            <Col span={5} start={6}>
              <div>
                <h2 className="h2Larger">We're socially, ethically and environmentally engaged</h2>
                <p className="textMarginBottomLarge">
                  We actively support each other, we run our business with integrity, and we take positive steps to preserve our environment. This empathy shines through our client work – both in the way we collaborate and in the results.
                </p>
                <p>
                  <Link to="/our-story/" className="button buttonFill">Read our story</Link>
                </p>
              </div>
            </Col>
          </Grid>
        </Section>

        <ContactBanner />

      </div>
      
    </article>
  );
};

export default Home;

export const Head = ({ data }) => {
  const { title, description } = data.content.frontmatter;
  return <Meta title={title} description={description} imgsrc={data.content.thumbnail && data.content.thumbnail.publicURL} />;
}

export const query = graphql`
  query {
    content: mdx(frontmatter: { name: { eq: "home" } }) {
      ...PageDefault
    }
    loreal: mdx(frontmatter: { name: { eq: "loreal" } }) {
      ...PageDefault
    }
    inflection: mdx(frontmatter: { name: { eq: "inflection" } }) {
      ...PageDefault
    }
    klir: mdx(frontmatter: { name: { eq: "klir" } }) {
      ...PageDefault
    }
    discover: mdx(frontmatter: { name: { eq: "discover" } }) {
      ...PageDefault
    }
    pilot: mdx(frontmatter: { name: { eq: "pilot" } }) {
      ...PageDefault
    }
    launch: mdx(frontmatter: { name: { eq: "launch" } }) {
      ...PageDefault
    }
    boost: mdx(frontmatter: { name: { eq: "boost" } }) {
      ...PageDefault
    }
    ai: mdx(frontmatter: {name: { eq: "ai" } }) {
      ...PageDefault
    }
    quote01: markdownRemark(frontmatter: { name: { eq: "thebodycoach-quote03" } }) {
      frontmatter {
        quote
        author
        authorTitle
      }
    }
    quote02: markdownRemark(frontmatter: { name: { eq: "coop-quote01" } }) {
      frontmatter {
        quote
        author
        authorTitle
      }
    }
    quote03: markdownRemark(frontmatter: { name: { eq: "three-quote01" } }) {
      frontmatter {
        quote
        author
        authorTitle
      }
    }
    bCorpLogo: file(relativePath: { eq: "logo-b-corp-black.png" }) {
      childrenImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
      }
    }
    logos: contentfulImagesList(name: {eq: "ustwoClientLogos"}) {
      images {
        title
        file {
          url
          contentType
        }
        gatsbyImageData
      }
    }
  }
`;
